import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Trash2, Minus, Plus } from "lucide-react";
import { api, fileUrl, errMsg } from "@/lib/api";
import { getCart, updateQty, removeItem } from "@/lib/cart";

export default function Cart() {
  const nav = useNavigate();
  const [items, setItems] = useState(getCart());
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");

  const refresh = useCallback(() => {
    const cart = getCart();
    setItems(cart);
    if (!cart.length) { setQuote(null); return; }
    api.post("/marketplace/quote", { items: cart.map((i) => ({ product_id: i.product_id, variation_id: i.variation_id, qty: i.qty, meta: i.meta })) })
      .then((r) => { setQuote(r.data); setErr(""); })
      .catch((e) => { setQuote(null); setErr(errMsg(e)); });
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener("gcv-cart", refresh);
    return () => window.removeEventListener("gcv-cart", refresh);
  }, [refresh]);

  if (!items.length) return (
    <div className="py-32 text-center" data-testid="cart-empty">
      <ShoppingCart className="mx-auto mb-4 text-zinc-700" size={48} />
      <p className="text-zinc-400 mb-6">Your cart is empty.</p>
      <Link to="/marketplace" className="bg-rose-500 hover:bg-rose-600 font-bold px-6 py-3 rounded-full">Browse the marketplace</Link>
    </div>
  );

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 min-h-screen" data-testid="cart-page">
      <h1 className="font-display font-extrabold text-3xl md:text-4xl mb-8">Your cart</h1>
      {err && <div className="bg-rose-500/10 border border-rose-500/40 text-rose-300 rounded-xl px-4 py-3 text-sm mb-6" data-testid="cart-error">{err}</div>}
      <div className="grid lg:grid-cols-3 gap-8 items-start">
        <div className="lg:col-span-2 space-y-3">
          {items.map((it, idx) => (
            <div key={`${it.product_id}|${it.variation_id || ""}|${it.meta?.schedule_id || it.meta?.schedule_date || ""}`} className="flex gap-4 bg-[#111] border border-white/10 rounded-2xl p-4" data-testid={`cart-item-${idx}`}>
              <Link to={`/marketplace/${it.slug}`} className="w-20 h-20 rounded-xl overflow-hidden bg-[#0a0a0a] shrink-0">
                {it.image && <img src={fileUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />}
              </Link>
              <div className="flex-1 min-w-0">
                <p className="font-bold leading-snug">{it.name}</p>
                {it.variation_name && <p className="text-xs text-zinc-500">{it.variation_name}</p>}
                {it.meta?.schedule_date && <p className="text-xs text-zinc-500">{it.meta.schedule_date} {it.meta.schedule_time}</p>}
                <p className="text-rose-400 font-bold text-sm mt-1">${Number(it.price).toFixed(2)}</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button onClick={() => removeItem(idx)} className="text-zinc-500 hover:text-rose-400" data-testid={`cart-remove-${idx}`}><Trash2 size={16} /></button>
                <div className="flex items-center border border-white/15 rounded-full">
                  <button onClick={() => updateQty(idx, it.qty - 1)} className="p-1.5" data-testid={`cart-minus-${idx}`}><Minus size={12} /></button>
                  <span className="w-7 text-center text-sm font-bold">{it.qty}</span>
                  <button onClick={() => updateQty(idx, it.qty + 1)} className="p-1.5" data-testid={`cart-plus-${idx}`}><Plus size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#111] border border-white/10 rounded-2xl p-6 lg:sticky lg:top-24" data-testid="cart-summary">
          <h2 className="font-display font-bold text-lg mb-4">Order summary</h2>
          {quote ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-zinc-300"><span>Subtotal</span><span data-testid="cart-subtotal">${quote.subtotal.toFixed(2)}</span></div>
              {quote.shipping > 0 && <div className="flex justify-between text-zinc-300"><span>Shipping</span><span>${quote.shipping.toFixed(2)}</span></div>}
              {quote.buyer_fee > 0 && <div className="flex justify-between text-zinc-300"><span>Service fee</span><span>${quote.buyer_fee.toFixed(2)}</span></div>}
              {quote.tax > 0 && <div className="flex justify-between text-zinc-300"><span>Tax</span><span>${quote.tax.toFixed(2)}</span></div>}
              <div className="flex justify-between font-bold text-base pt-3 border-t border-white/10"><span>Total</span><span data-testid="cart-total">${quote.total.toFixed(2)}</span></div>
            </div>
          ) : <p className="text-sm text-zinc-500">{err ? "Fix the issue above to continue." : "Calculating..."}</p>}
          <button onClick={() => nav("/checkout")} disabled={!quote} data-testid="cart-checkout-btn"
            className="w-full mt-6 bg-rose-500 hover:bg-rose-600 disabled:opacity-40 font-bold py-3.5 rounded-full transition-colors">
            Proceed to checkout
          </button>
          <p className="text-[11px] text-zinc-500 mt-3 text-center">No hidden charges — everything you'll pay is shown above.</p>
        </div>
      </div>
    </div>
  );
}
